import Link from "next/link";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import { FooterProps } from "@lib/models/layout/footer";
import { IconPhone, IconMail, IconPin, IconClock, IconShield } from "@components/utils/icons";
import { directionsUrl, fullAddress } from "@lib/utils/geo";
import * as gtag from "@lib/utilities/analytics";

/**
 * The footer is where the page's darkness is spent: a full violet-700 band,
 * white text at 8.62 contrast (AAA). It closes the page rather than trailing
 * off into grey, and it is the second home for the navigation that came off
 * the top menu — FAQ and Careers (D16).
 *
 * The newsletter is deliberately not here (D15).
 */
export default function Footer(props: FooterProps) {
  const { t } = useTranslation("main");
  const year = new Date().getFullYear();

  // the bottom legal row comes from the CMS `footer-legal` menu (owner,
  // 2026-09-07) — NO hardcoded fallback, by owner decision: what the CMS
  // returns is what renders. CONVENTION: an item whose url is
  // "#cookie-settings" renders as the consent-settings BUTTON — it reopens
  // the banner via an event, it is not navigation.
  const legalItems = (props.legalMenu?.items ?? []) as { text?: string; url?: string }[];

  return (
    <footer className="footer-blob relative overflow-hidden bg-violet-700 -mt-4 px-6 pb-6 pt-16 text-white min-[1400px]:mt-0 min-[1400px]:pt-44">
      {/* brush as GRAFFITI (owner, 2026-09-07): one large sweep across the
          right half, vertically centered and oversized so every cut edge of
          the asset bleeds past the footer's edges — only the strokes' natural
          tips (the asset's left edge) remain visible. A small clipped copy
          read as "cut", not hand-made. */}
      <img loading="lazy" src="/images/brand/stroke-8.svg" alt="" aria-hidden="true"
        className="pointer-events-none absolute -right-16 top-1/2 w-[46rem] -translate-y-1/2 select-none opacity-[0.12] brightness-0 invert" />
      <div className="shell grid gap-10 md:grid-cols-3 min-[1400px]:max-[1500px]:pl-[4vw]">
        <div>
          {/* the stamp anchors the footer (approved design v8): always on its
              white plate — its inner arc is violet-700 and would vanish on
              this ground — and with room, because a hand-drawn circular mark
              dies when crowded */}
          {/* designer pass 2026-09-14: the stamp sits DIRECTLY on the violet
              ground — light ring (stamp-mono reads pale on violet-700) with
              the colour symbol centered inside; the white plate is gone */}
          {/* PSD-exact colours: ring + the symbol's violet arc both render as
              the pale #ad8dff on the purple ground (the #60c arc of
              symbol-color simply vanished here — «missing part», 2026-09-14) */}
          <span className="relative inline-block h-28 w-28">
            <Image src="/images/brand/stamp-script.svg" alt="" width={112} height={115}
              className="h-auto w-full" />
            <Image src="/images/brand/symbol-on-violet.svg" alt="Physiowave Therapy" width={38} height={54}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
          </span>
          {props.legalName ? (
            <p className="mt-4 text-sm opacity-95">{props.legalName}</p>
          ) : null}
          {props.eopyyContracted ? (
            <p className="mt-2 flex items-center gap-1.5 text-sm font-bold">
              <IconShield className="flex-none opacity-90" width={16} height={16} />
              {t("footer.eopyyContracted")}
            </p>
          ) : null}
        </div>

        {props.menu ? (
          <nav className="grid grid-cols-2 gap-8" aria-label={t("footer.sitemapAria")}>
            {props.menu.items.map((group, gi) => (
              <div key={gi}>
                <p className="mb-3 text-xs font-extrabold tracking-[0.08em] text-white opacity-70">{group.text}</p>
                <ul className="flex flex-col gap-2 text-[0.9375rem]">
                  {(group.subMenu?.items ?? []).map((item, i) => (
                    <li key={i}>
                      <a href={item.url ?? "#"} className="opacity-95 hover:underline">{item.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        ) : (
          <div />
        )}

        {/* contact rows carry the icon set: each icon disambiguates a bare
            string (a number, an address) at a glance — informational, not
            decorative. mt-0.5 optically aligns the 16px glyph to the text
            cap-height. */}
        <div className="space-y-2.5 text-[0.9375rem]">
          {props.telephone ? (
            <p className="flex items-start gap-2">
              <IconPhone className="mt-0.5 flex-none opacity-90" width={16} height={16} />
              <a className="font-bold hover:underline" href={`tel:${props.telephone.replace(/\s/g, "")}`}
                onClick={() => gtag.event("phone_click", { event_category: "conversion", event_label: "footer" })}>
                {props.telephone}
              </a>
            </p>
          ) : null}
          {props.email ? (
            <p className="flex items-start gap-2">
              <IconMail className="mt-0.5 flex-none opacity-90" width={16} height={16} />
              <a className="hover:underline" href={`mailto:${props.email}`}
                onClick={() => gtag.event("email_click", { event_category: "conversion", event_label: "footer" })}>{props.email}</a>
            </p>
          ) : null}
          {props.addressStreet ? (
            <p className="flex items-start gap-2 opacity-90">
              <IconPin className="mt-0.5 flex-none" width={16} height={16} />
              {/* the address IS the directions link (owner, 2026-09-07):
                  Google's dir API with no origin routes from the visitor's
                  current location — a chosen click, so D48 holds */}
              <a
                href={directionsUrl(fullAddress(props))}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => gtag.event("map_directions_click", { event_category: "engagement", event_label: "footer" })}
                className="hover:underline"
              >
                {props.addressStreet}
                {props.addressLocality ? `, ${props.addressLocality}` : ""}
                {props.addressPostalCode ? ` ${props.addressPostalCode}` : ""}
              </a>
            </p>
          ) : null}
          {props.workingHours ? (
            <p className="flex items-start gap-2 opacity-90">
              <IconClock className="mt-0.5 flex-none" width={16} height={16} />
              <span>{props.workingHours}</span>
            </p>
          ) : null}
        </div>
      </div>

      <div className="shell mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-white/20 pt-5 text-[0.8125rem] min-[1400px]:max-[1500px]:pl-[4vw]">
        {/* © + agency credit stacked LEFT, the legal menu stays RIGHT on the
            same row (owner, 2026-09-07 — the menu must not wrap below) */}
        <div className="flex flex-col gap-0.5">
          <span className="opacity-70">
            © {year} {props.legalName ?? "Physiowave Therapy"}
          </span>
          {/* opacity-80, not 60: the quieter tint failed WCAG 4.5:1 on the
              violet ground (the one a11y flag in the Lighthouse audit) */}
          <a href="https://agilevendors.com/" target="_blank" rel="noopener noreferrer"
            className="opacity-80 hover:underline">
            {t("footer.credits.agilevendors")}
          </a>
        </div>
        <nav className="flex flex-wrap gap-5">
          {legalItems.map((item, i) =>
            item.url === "#cookie-settings" ? (
              <button key={i} type="button" className="font-bold hover:underline"
                onClick={() => window.dispatchEvent(new CustomEvent("pw-open-consent"))}>
                {item.text}
              </button>
            ) : (
              /* prefetch={false}: chrome links must not prefetch page data
                 into the LCP bandwidth window (see header.tsx) */
              <Link key={i} prefetch={false} className="font-bold hover:underline" href={item.url ?? "#"}>
                {item.text}
              </Link>
            )
          )}
        </nav>
      </div>
    </footer>
  );
}
